import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  CurrencyDollarIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  SparklesIcon,
  GlobeAltIcon,
  ArrowTopRightOnSquareIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';

const NDHTokenPage: React.FC = () => {
  const tokenAddress = '0x7890123456789012345678901234567890123456';

  const tokenInfo = [
    { label: 'Token Name', value: 'NARDIHA Token' },
    { label: 'Symbol', value: 'NDH' },
    { label: 'Network', value: 'Polygon' },
    { label: 'Total Supply', value: '1,000,000,000' },
    { label: 'Decimals', value: '18' },
    { label: 'Token Type', value: 'ERC-20' },
  ];

  const tokenUtility = [
    {
      icon: CurrencyDollarIcon,
      title: 'Platform Fees',
      description: 'Pay transaction fees across all NARDIHA platforms with discounts up to 35%'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Staking',
      description: 'Stake NDH to unlock tiered benefits including fee discounts and exclusive access'
    },
    {
      icon: UserGroupIcon,
      title: 'Governance',
      description: 'Vote on proposals and shape the future of the NARDIHA ecosystem'
    },
    {
      icon: SparklesIcon,
      title: 'Rewards',
      description: 'Earn NDH through staking rewards, liquidity mining, and platform activities'
    },
    {
      icon: GlobeAltIcon,
      title: 'Cross-Platform',
      description: 'Use NDH across 50+ platforms in the NARDIHA ecosystem'
    },
    {
      icon: BanknotesIcon,
      title: 'Liquidity',
      description: 'Provide liquidity to NDH pairs and earn trading fees'
    }
  ];

  const distribution = [
    { category: 'Public Sale', percentage: 25, amount: '250,000,000', color: 'bg-primary-500' },
    { category: 'Ecosystem & Rewards', percentage: 30, amount: '300,000,000', color: 'bg-green-500' },
    { category: 'Team & Advisors', percentage: 15, amount: '150,000,000', color: 'bg-blue-500' },
    { category: 'Treasury', percentage: 15, amount: '150,000,000', color: 'bg-purple-500' },
    { category: 'Liquidity', percentage: 10, amount: '100,000,000', color: 'bg-yellow-500' },
    { category: 'Strategic Partners', percentage: 5, amount: '50,000,000', color: 'bg-pink-500' },
  ];

  const vesting = [
    { category: 'Team & Advisors', schedule: '4-year vesting with 1-year cliff' },
    { category: 'Strategic Partners', schedule: '2-year vesting with 6-month cliff' },
    { category: 'Treasury', schedule: 'DAO-controlled release' },
    { category: 'Ecosystem Rewards', schedule: 'Released over 5 years based on platform activity' },
  ];

  const exchanges = [
    { name: 'Uniswap', type: 'DEX', pair: 'NDH/MATIC' },
    { name: 'QuickSwap', type: 'DEX', pair: 'NDH/USDC' },
    { name: 'SushiSwap', type: 'DEX', pair: 'NDH/ETH' },
  ];

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-b from-dark-900 to-dark-950 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary-500/20 rounded-full blur-3xl" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="w-24 h-24 bg-gradient-to-br from-primary-500 to-purple-600 rounded-full flex items-center justify-center">
                <span className="text-3xl font-bold text-white">NDH</span>
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
              NDH Token
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              The native utility and governance token powering the entire NARDIHA ecosystem
            </p>

            {/* Token Address */}
            <div className="inline-flex items-center gap-3 px-4 py-2 bg-dark-800 rounded-xl mb-8">
              <span className="text-gray-400 text-sm">Contract:</span>
              <code className="text-primary-400 text-sm font-mono">{tokenAddress.slice(0, 10)}...{tokenAddress.slice(-8)}</code>
              <button
                onClick={() => navigator.clipboard.writeText(tokenAddress)}
                className="p-1 hover:bg-dark-700 rounded"
              >
                <DocumentDuplicateIcon className="w-4 h-4 text-gray-400" />
              </button>
              <a
                href={`https://polygonscan.com/token/${tokenAddress}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1 hover:bg-dark-700 rounded"
              >
                <ArrowTopRightOnSquareIcon className="w-4 h-4 text-gray-400" />
              </a>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-dark-800/50 rounded-xl p-4 border border-dark-700">
                <p className="text-2xl font-bold text-white">$0.45</p>
                <p className="text-gray-400 text-sm">Current Price</p>
              </div>
              <div className="bg-dark-800/50 rounded-xl p-4 border border-dark-700">
                <p className="text-2xl font-bold text-white">$450M</p>
                <p className="text-gray-400 text-sm">Market Cap</p>
              </div>
              <div className="bg-dark-800/50 rounded-xl p-4 border border-dark-700">
                <p className="text-2xl font-bold text-green-400">+12.5%</p>
                <p className="text-gray-400 text-sm">24h Change</p>
              </div>
              <div className="bg-dark-800/50 rounded-xl p-4 border border-dark-700">
                <p className="text-2xl font-bold text-white">$25M</p>
                <p className="text-gray-400 text-sm">24h Volume</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Token Info */}
      <section className="py-16 bg-dark-900/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Token Information</h2>
            <div className="bg-dark-800/50 rounded-xl border border-dark-700 overflow-hidden">
              <div className="grid md:grid-cols-2">
                {tokenInfo.map((info, idx) => (
                  <div
                    key={idx}
                    className={`flex justify-between p-4 ${idx < tokenInfo.length - 1 ? 'border-b' : ''} ${idx % 2 === 0 ? 'md:border-r' : ''} border-dark-700`}
                  >
                    <span className="text-gray-400">{info.label}</span>
                    <span className="text-white font-medium">{info.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Token Utility */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Token Utility</h2>
              <p className="text-gray-400">NDH is the fuel that powers the entire NARDIHA ecosystem</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tokenUtility.map((utility, idx) => {
                const Icon = utility.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="bg-dark-800/50 rounded-xl p-6 border border-dark-700"
                  >
                    <Icon className="w-10 h-10 text-primary-400 mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">{utility.title}</h3>
                    <p className="text-gray-400">{utility.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Token Distribution */}
      <section className="py-16 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Token Distribution</h2>
              <p className="text-gray-400">Total Supply: 1,000,000,000 NDH</p>
            </div>

            {/* Distribution Chart */}
            <div className="bg-dark-800/50 rounded-xl p-8 border border-dark-700 mb-8">
              <div className="flex h-8 rounded-full overflow-hidden mb-6">
                {distribution.map((item, idx) => (
                  <div
                    key={idx}
                    className={`${item.color}`}
                    style={{ width: `${item.percentage}%` }}
                    title={`${item.category}: ${item.percentage}%`}
                  />
                ))}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {distribution.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded ${item.color}`} />
                      <span className="text-gray-300">{item.category}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-white font-medium">{item.percentage}%</span>
                      <span className="text-gray-500 text-sm ml-2">({item.amount})</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Vesting Schedule */}
            <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
              <h3 className="text-xl font-semibold text-white mb-4">Vesting Schedule</h3>
              <div className="space-y-3">
                {vesting.map((item, idx) => (
                  <div key={idx} className="flex justify-between py-2 border-b border-dark-700 last:border-0">
                    <span className="text-gray-400">{item.category}</span>
                    <span className="text-white">{item.schedule}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Where to Buy */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Where to Buy NDH</h2>
              <p className="text-gray-400">Available on leading decentralized exchanges</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {exchanges.map((exchange, idx) => (
                <motion.a
                  key={idx}
                  href="#"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-dark-800/50 rounded-xl p-6 border border-dark-700 hover:border-primary-500/50 transition-colors text-center"
                >
                  <div className="w-16 h-16 bg-dark-700 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <ChartBarIcon className="w-8 h-8 text-primary-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-1">{exchange.name}</h3>
                  <p className="text-gray-400 text-sm mb-2">{exchange.type}</p>
                  <span className="text-primary-400 text-sm">{exchange.pair}</span>
                </motion.a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-primary-500/20 to-purple-500/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <ArrowTrendingUpIcon className="w-12 h-12 text-primary-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-4">
              Start Using NDH Today
            </h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Stake your NDH tokens to unlock premium benefits across the entire NARDIHA ecosystem.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/staking"
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors"
              >
                Stake NDH
              </Link>
              <Link
                to="/governance"
                className="px-8 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-xl transition-colors"
              >
                Participate in Governance
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NDHTokenPage;
